import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsent } from "@/components/CookieConsent";
import {
  CinematicHero,
  SectionHead,
  Reveal,
  FullBleedQuote,
  SplitFeature,
  StatStripe,
  CinematicCTA,
  CinematicFAQ,
  TripleStrip,
} from "@/components/service/Cinematic";
import heroImg from "@/assets/painter-work.jpg";
import int1 from "@/assets/interior-ref-1.jpg";
import int2 from "@/assets/interior-ref-2.jpeg";
import int3 from "@/assets/interior-ref-3.jpeg";
import int4 from "@/assets/interior-ref-4.jpeg";
import int5 from "@/assets/interior-ref-5.jpeg";
import team from "@/assets/team-ceiling-work.jpg";

const qStufen = [
  { stufe: "Q1", name: "Grundverspachtelung", desc: "Für überputzte oder hinterfüllte Oberflächen, nicht für Anstrich geeignet." },
  { stufe: "Q2", name: "Standard-Spachtelung", desc: "Für normale Innenanstriche – streiflicht­unauffällig." },
  { stufe: "Q3", name: "Feinspachtelung", desc: "Erhöhte Anforderung, z. B. für seiden­glänzende Anstriche." },
  { stufe: "Q4", name: "Glattstrich", desc: "Höchste Stufe – streiflichttauglich, ideal für Design­farben und Loftoptik." },
];

const faqs = [
  { q: "Was kostet Malerarbeiten für eine Wohnung in Herford?", a: "Ein kompletter Innenanstrich für eine 3-Zimmer-Wohnung (ca. 80 m² Wohnfläche) liegt je nach Zustand und Qualitätsstufe zwischen 1.500 und 4.000 €. Bei starken Schäden oder Q4-Spachtelung entsprechend mehr. Festpreisangebot kostenlos." },
  { q: "Wie lange dauert die Renovierung einer Wohnung?", a: "Eine 3-Zimmer-Wohnung mit Standardanstrich in 3–5 Werktagen. Mit Tapezieren, Q3/Q4 oder Bodenarbeiten 1–2 Wochen. Wir planen den Ablauf so, dass Sie schnellstmöglich wieder einziehen können." },
  { q: "Arbeiten Sie auch in bewohnten Wohnungen?", a: "Ja – Zimmer für Zimmer. Möbel und Böden werden professionell geschützt, wir arbeiten mit geruchsarmen Materialien. Ablauf und Zeiten werden vorab abgestimmt." },
  { q: "Auch gewerbliche Räume außerhalb der Öffnungszeiten?", a: "Ja. Büros, Praxen, Restaurants und Läden renovieren wir abends, nachts oder am Wochenende, damit Ihr Betrieb nicht stillsteht." },
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Malerarbeiten & Innensanierung",
  name: "Malerarbeiten & Innensanierung Herford",
  provider: { "@type": "LocalBusiness", name: "Malerfachbetrieb Stein GmbH & Co. KG" },
  areaServed: "Herford",
  description: "Professionelle Malerarbeiten in Herford: Q4-Spachtelung, Tapezieren, Wandgestaltung und Komplettrenovierung.",
};

const InnensanierungMalerarbeitenHerford = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Malerarbeiten in Herford: Wände in Q4-Meisterqualität"
      description="Malerarbeiten & Innensanierung in Herford: Q4-Spachtelung, Tapezieren & Wandgestaltung. Sauber, termintreu, Festpreis-Garantie vom Meisterbetrieb. ☎ 05221 3832130"
      keywords="Malerarbeiten Herford, Innensanierung Herford, Wohnung renovieren Herford, Tapezieren Herford, Q4 Spachtelung Herford, Wandgestaltung Herford, Maler Festpreis Herford"
      canonical="https://malerfachbetrieb-stein.de/innensanierung-malerarbeiten-herford"
      structuredData={structuredData}
    />
    <Navigation />
    <WhatsAppButton />

    <main>
      <CinematicHero
        chapter="Kapitel 05 / Innen"
        eyebrow="Malerarbeiten & Innensanierung"
        title={<>Wände, die <em className="not-italic text-background/70">ruhig</em> wirken – auch im Streiflicht.</>}
        subtitle="Von der sorgfältigen Untergrundvorbereitung bis zum perfekten Finish. Wir renovieren Wohnungen, Häuser und Gewerbeobjekte in Herford mit handwerklicher Präzision."
        image={heroImg}
        imageAlt="Malerarbeiten Innensanierung Herford"
        ctaLabel="Kostenloses Angebot anfragen"
      />

      <StatStripe
        stats={[
          { value: "Q4", label: "Höchste Spachtel­qualität" },
          { value: "3–5", label: "Werktage / Wohnung" },
          { value: "10+", label: "Jahre Erfahrung" },
          { value: "24h", label: "Rückmeldung" },
        ]}
      />

      <FullBleedQuote
        image={team}
        imageAlt="Maler bei Deckenarbeit"
        quote={<>„Gute Wände beginnen nicht beim Anstrich – sie beginnen beim Untergrund.“</>}
        caption="Werkstattprinzip"
      />

      {/* Leistungen */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Leistungen"
            title={<>Untergrund, Fläche,<br /> Finish – in einer Hand.</>}
          />
          <div className="space-y-24 md:space-y-32">
            <SplitFeature
              index="01"
              eyebrow="Anstrich"
              title="Wand- und Deckenanstriche in Meisterqualität."
              body="Hochwertige Innenanstriche mit Materialien von Brillux, Caparol und Sto. Wir arbeiten sauber, schützen Böden und Einrichtung und übergeben eine Baustelle, die aussieht, als wären wir nie da gewesen."
              bullets={["Brillux / Caparol / Sto", "Schutzabdeckung inklusive", "Geruchsarme Materialien"]}
              image={int1}
              imageAlt="Wand- und Deckenanstrich Herford"
            />
            <SplitFeature
              reverse
              index="02"
              eyebrow="Spachtel"
              title="Q2 bis Q4 – wenn Licht alles sichtbar macht."
              body="Q4 ist der höchste Glattheits­grad. Wir spachteln von Q2 (Standard) bis Q4 (streiflicht­tauglich), je nach Anforderung an Material und Lichteinfall."
              image={int2}
              imageAlt="Spachtelarbeiten Q4 Glattstrich"
            />
            <SplitFeature
              index="03"
              eyebrow="Tapeten & Gestaltung"
              title="Tapezieren, das streifenfrei bleibt."
              body="Raufaser, Vlies, Strukturtapete, Designtapete – wir tapezieren sauber, streifenfrei und dauerhaft. Auch Muster- und Fototapeten auf Anfrage."
              image={int3}
              imageAlt="Tapezieren Designtapete"
            />
          </div>
        </div>
      </section>

      {/* Q-Stufen */}
      <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
        <div className="container mx-auto">
          <SectionHead
            invert
            eyebrow="Qualitätsstufen"
            title="Q1 bis Q4 – was die Stufen wirklich bedeuten."
            intro="Spachtelqualität entscheidet, wie Wände im Streiflicht wirken. Wir empfehlen die Stufe, die zu Material, Beleuchtung und Anspruch passt – nicht die teuerste."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-background/10">
            {qStufen.map((q, i) => (
              <Reveal key={q.stufe} delay={i * 80}>
                <div className="bg-foreground p-8 md:p-10 h-full">
                  <div className="font-caslon text-6xl font-bold text-background mb-4">{q.stufe}</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-background/50 mb-2">Stufe {i + 1}</div>
                  <h3 className="font-semibold mb-3 text-background">{q.name}</h3>
                  <p className="text-sm text-background/65 leading-relaxed">{q.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TripleStrip
        images={[
          { src: int4, alt: "Wohnungsrenovierung Herford", caption: "Wohnungsrenovierung" },
          { src: team, alt: "Deckenarbeiten", caption: "Decken- & Wandarbeiten" },
          { src: int5, alt: "Designanstrich Wohnraum", caption: "Designanstrich Wohnraum" },
        ]}
      />

      <CinematicFAQ
        faqs={faqs}
        title="Fragen zu Malerarbeiten."
      />

      <CinematicCTA
        image={heroImg}
        imageAlt="Malerarbeiten Herford"
        title={<>Ihr Zuhause verdient<br /> das Beste.</>}
        body="Kostenloses Angebot innerhalb 24 Stunden – Festpreis, klar dokumentiert."
        ctaLabel="Angebot anfragen"
      />
    </main>

    <Footer />
    <CookieConsent />
  </div>
);

export default InnensanierungMalerarbeitenHerford;